import {
  ATTACK_COMPLEXITY,
  ATTACK_VECTOR,
  CVSS4_ATTACK_COMPLEXITY,
  CVSS4_ATTACK_REQUIREMENTS,
  CVSS4_ATTACK_VECTOR,
  CVSS4_IMPACT_METRICS,
  CVSS4_PRIVILEGES_REQUIRED,
  CVSS4_USER_INTERACTION,
  IMPACT_METRICS,
  PRIVILEGES_REQUIRED,
  SCOPE,
  USER_INTERACTION,
  getCvssVersionFromVector,
  parseVectorString,
  readCvssVectorString,
  toNonEmptyString,
  type FindingCvssFields,
} from '@terra/shared-cvss';
import { isObject } from 'radash';

export type CvssMetricChange = {
  field: string;
  beforeLabel: string;
  afterLabel: string;
};

const CVSS3_METRIC_FIELDS = [
  'attackVector',
  'attackComplexity',
  'privilegesRequired',
  'userInteraction',
  'scope',
  'confidentiality',
  'integrity',
  'availability',
] as const;

const CVSS4_METRIC_FIELDS = [
  'attackVector',
  'attackComplexity',
  'attackRequirements',
  'privilegesRequired',
  'userInteraction',
  'vulnConfidentiality',
  'vulnIntegrity',
  'vulnAvailability',
  'subConfidentiality',
  'subIntegrity',
  'subAvailability',
] as const;

const CVSS3_METRIC_OPTIONS: Record<
  (typeof CVSS3_METRIC_FIELDS)[number],
  readonly { label: string; value: string }[]
> = {
  attackVector: ATTACK_VECTOR,
  attackComplexity: ATTACK_COMPLEXITY,
  privilegesRequired: PRIVILEGES_REQUIRED,
  userInteraction: USER_INTERACTION,
  scope: SCOPE,
  confidentiality: IMPACT_METRICS,
  integrity: IMPACT_METRICS,
  availability: IMPACT_METRICS,
};

const CVSS4_METRIC_OPTIONS: Record<
  (typeof CVSS4_METRIC_FIELDS)[number],
  readonly { label: string; value: string }[]
> = {
  attackVector: CVSS4_ATTACK_VECTOR,
  attackComplexity: CVSS4_ATTACK_COMPLEXITY,
  attackRequirements: CVSS4_ATTACK_REQUIREMENTS,
  privilegesRequired: CVSS4_PRIVILEGES_REQUIRED,
  userInteraction: CVSS4_USER_INTERACTION,
  vulnConfidentiality: CVSS4_IMPACT_METRICS,
  vulnIntegrity: CVSS4_IMPACT_METRICS,
  vulnAvailability: CVSS4_IMPACT_METRICS,
  subConfidentiality: CVSS4_IMPACT_METRICS,
  subIntegrity: CVSS4_IMPACT_METRICS,
  subAvailability: CVSS4_IMPACT_METRICS,
};

function resolveOptionLabel(
  options: readonly { label: string; value: string }[],
  value: string,
): string {
  return options.find((option) => option.value === value)?.label ?? value;
}

function cvssScoreLabel(cvss: unknown): string {
  return (
    toNonEmptyString(
      isObject(cvss) ? (cvss as FindingCvssFields).score : undefined,
    ) ?? ''
  );
}

export function hasCvssVectorChanged({
  before,
  after,
}: {
  before: unknown;
  after: unknown;
}): boolean {
  const beforeVector = readCvssVectorString(before);
  const afterVector = readCvssVectorString(after);

  return Boolean(beforeVector && afterVector && beforeVector !== afterVector);
}

export function hasCvssScoreChanged({
  before,
  after,
}: {
  before: unknown;
  after: unknown;
}): boolean {
  const beforeScore = cvssScoreLabel(before);
  const afterScore = cvssScoreLabel(after);

  return Boolean(beforeScore && afterScore && beforeScore !== afterScore);
}

export function resolveCvssSuggestionBefore({
  pendingBefore,
  pendingAfter,
  original,
}: {
  pendingBefore: unknown;
  pendingAfter: unknown;
  original?: unknown;
}): unknown {
  if (
    hasCvssVectorChanged({ before: pendingBefore, after: pendingAfter }) ||
    hasCvssScoreChanged({ before: pendingBefore, after: pendingAfter })
  ) {
    return pendingBefore;
  }

  if (
    original !== undefined &&
    (hasCvssVectorChanged({ before: original, after: pendingAfter }) ||
      hasCvssScoreChanged({ before: original, after: pendingAfter }))
  ) {
    return original;
  }

  return pendingBefore;
}

export function getCvssScoreTransition({
  before,
  after,
}: {
  before: unknown;
  after: unknown;
}): { beforeLabel: string; afterLabel: string } | null {
  if (!hasCvssScoreChanged({ before, after })) {
    return null;
  }

  const beforeScore = cvssScoreLabel(before);
  const afterScore = cvssScoreLabel(after);

  if (!beforeScore || !afterScore) {
    return null;
  }

  return {
    beforeLabel: beforeScore,
    afterLabel: afterScore,
  };
}

export function getCvssMetricChanges({
  before,
  after,
  version,
}: {
  before: unknown;
  after: unknown;
  version: '3.1' | '4.0';
}): CvssMetricChange[] {
  const beforeVector = readCvssVectorString(before);
  const afterVector = readCvssVectorString(after);

  if (!beforeVector || !afterVector) {
    return [];
  }

  const resolvedVersion =
    getCvssVersionFromVector(afterVector) ??
    getCvssVersionFromVector(beforeVector) ??
    version;
  const beforeMetrics = parseVectorString(beforeVector);
  const afterMetrics = parseVectorString(afterVector);

  if (resolvedVersion === '4.0') {
    return CVSS4_METRIC_FIELDS.flatMap((field) => {
      const beforeValue = beforeMetrics[field];
      const afterValue = afterMetrics[field];

      if (!beforeValue || !afterValue || beforeValue === afterValue) {
        return [];
      }

      return [
        {
          field,
          beforeLabel: resolveOptionLabel(
            CVSS4_METRIC_OPTIONS[field],
            beforeValue,
          ),
          afterLabel: resolveOptionLabel(
            CVSS4_METRIC_OPTIONS[field],
            afterValue,
          ),
        },
      ];
    });
  }

  return CVSS3_METRIC_FIELDS.flatMap((field) => {
    const beforeValue = beforeMetrics[field];
    const afterValue = afterMetrics[field];

    if (!beforeValue || !afterValue || beforeValue === afterValue) {
      return [];
    }

    return [
      {
        field,
        beforeLabel: resolveOptionLabel(
          CVSS3_METRIC_OPTIONS[field],
          beforeValue,
        ),
        afterLabel: resolveOptionLabel(CVSS3_METRIC_OPTIONS[field], afterValue),
      },
    ];
  });
}
